import { Injectable } from '@angular/core';

export interface DomReadyOptions {
  /**
   * Delay in milliseconds before executing the callback
   * @default 0
   */
  delay?: number;
  
  /**
   * Whether to log debug information
   * @default false
   */
  debug?: boolean;
  
  /**
   * Custom condition to check before executing callback
   * @default undefined
   */
  condition?: () => boolean;
  
  /**
   * Maximum number of retry attempts if condition fails
   * @default 3
   */
  maxRetries?: number;
  
  /**
   * Delay between retry attempts in milliseconds
   * @default 100
   */
  retryDelay?: number;
}

@Injectable({
  providedIn: 'root'
})
export class DomReadyService {

  /**
   * Executes a callback function after ensuring DOM is ready and hydration is complete
   * @param callback The function to execute
   * @param options Configuration options
   * @returns Promise that resolves when callback is executed or rejects on error
   */
  whenReady(
    callback: () => void | Promise<void>,
    options: DomReadyOptions = {}
  ): Promise<void> {
    const {
      delay = 0,
      debug = false,
      condition,
      maxRetries = 3,
      retryDelay = 100
    } = options;

    return new Promise((resolve, reject) => {
      if (debug) {
        this.logDebugInfo();
      }

      const executeCallback = async (retryCount = 0): Promise<void> => {
        try {
          // Check custom condition if provided
          if (condition && !condition()) {
            if (retryCount < maxRetries) {
              if (debug) {
                console.log(`DomReadyService: Condition not met, retrying in ${retryDelay}ms (attempt ${retryCount + 1}/${maxRetries})`);
              }
              setTimeout(() => executeCallback(retryCount + 1), retryDelay);
              return;
            } else {
              throw new Error('Maximum retry attempts reached - condition not met');
            }
          }

          if (debug) {
            console.log('DomReadyService: Executing callback after hydration delay');
          }

          const result = callback();
          
          // Handle async callbacks
          if (result instanceof Promise) {
            await result;
          }

          if (debug) {
            console.log('DomReadyService: Callback executed successfully');
          }

          resolve();
        } catch (error) {
          if (debug) {
            console.error('DomReadyService: Error executing callback:', error);
          }
          reject(error);
        }
      };

      // Use setTimeout to ensure hydration is complete
      setTimeout(() => executeCallback(), delay);
    });
  }

  /**
   * Executes DOM manipulation on elements found by selector
   * @param selector CSS selector to find elements
   * @param manipulator Function that receives found elements
   * @param options Configuration options
   * @returns Promise that resolves when manipulation is complete
   */
  manipulateElements<T extends Element = Element>(
    selector: string,
    manipulator: (elements: T[]) => void | Promise<void>,
    options: DomReadyOptions & { 
      /**
       * Root element to search within
       * @default document
       */
      root?: Element | Document;
      /**
       * Whether to throw error if no elements found
       * @default false
       */
      requireElements?: boolean;
    } = {}
  ): Promise<void> {
    const {
      root = document,
      requireElements = false,
      ...baseOptions
    } = options;

    return this.whenReady(() => {
      const elements = Array.from(root.querySelectorAll<T>(selector));
      
      if (requireElements && elements.length === 0) {
        throw new Error(`No elements found for selector: ${selector}`);
      }

      if (baseOptions.debug) {
        console.log(`DomReadyService: Found ${elements.length} elements for selector "${selector}"`);
      }

      return manipulator(elements);
    }, {
      ...baseOptions,
      condition: options.condition || (() => {
        const elements = root.querySelectorAll(selector);
        return elements.length > 0 || !requireElements;
      })
    });
  }

  /**
   * Waits for specific elements to be available in the DOM
   * @param selector CSS selector to wait for
   * @param options Configuration options
   * @returns Promise that resolves with found elements
   */
  waitForElements<T extends Element = Element>(
    selector: string,
    options: DomReadyOptions & {
      root?: Element | Document;
      minCount?: number;
    } = {}
  ): Promise<T[]> {
    const {
      root = document,
      minCount = 1,
      ...baseOptions
    } = options;

    return new Promise((resolve, reject) => {
      this.whenReady(() => {
        const elements = Array.from(root.querySelectorAll<T>(selector));
        
        if (elements.length >= minCount) {
          resolve(elements);
        } else {
          throw new Error(`Expected at least ${minCount} elements, found ${elements.length}`);
        }
      }, {
        ...baseOptions,
        condition: () => {
          const elements = root.querySelectorAll(selector);
          return elements.length >= minCount;
        }
      }).catch(reject);
    });
  }

  /**
   * Always returns true since AnalogJS handles platform detection internally
   */
  get isInBrowser(): boolean {
    return true;
  }

  private logDebugInfo(): void {
    console.log('=== DomReadyService DEBUG INFO ===');
    console.log('typeof window:', typeof window);
    console.log('typeof document:', typeof document);
    console.log('document.readyState:', typeof document !== 'undefined' ? document.readyState : 'undefined');
  }
}